// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { SuperadminService } from "@/services/superadmin";
import { UserFilters } from "@/components/superadmin/users/UserFilters";
import { UserTable } from "@/components/superadmin/users/UserTable";
import { UserDetail } from "@/components/superadmin/users/UserDetail";
import { BanUserDialog } from "@/components/superadmin/users/BanUserDialog";
import { DeleteUserDialog } from "@/components/superadmin/users/DeleteUserDialog";
import { ToggleSuperadminDialog } from "@/components/superadmin/users/ToggleSuperadminDialog";
import type { SuperadminUser } from "@/types/superadmin";

export interface SearchParams {
  page: number;
  search: string;
  status: string;
  role: string;
  twoFactor: string;
  sortBy: string;
  sortOrder: string;
}

export const Route = createFileRoute("/superadmin/users")({
  component: UsersPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || "",
    status: (search.status as string) || "all",
    role: (search.role as string) || "all",
    twoFactor: (search.twoFactor as string) || "all",
    sortBy: (search.sortBy as string) || "createdAt",
    sortOrder: (search.sortOrder as string) || "desc",
  }),
});

function UsersPage() {
  const { t } = useTranslation("superadmin");
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const params = Route.useSearch();

  // Debounced search
  const [searchInput, setSearchInput] = useState(params.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchInput !== params.search) {
        updateParams({ search: searchInput, page: 1 });
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Sync search input when URL changes externally
  useEffect(() => {
    setSearchInput(params.search);
  }, [params.search]);

  const updateParams = useCallback(
    (updates: Partial<SearchParams>) => {
      navigate({
        search: { ...params, ...updates },
        replace: true,
      });
    },
    [navigate, params],
  );

  // Query
  const { data, isLoading } = useQuery({
    queryKey: ["superadmin", "users", params],
    queryFn: () => SuperadminService.listUsers(params),
  });

  // Dialog state
  const [selectedUser, setSelectedUser] = useState<SuperadminUser | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [superadminDialogOpen, setSuperadminDialogOpen] = useState(false);

  const invalidateUsers = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["superadmin", "users"] });
  }, [queryClient]);

  // Mutations
  const banMutation = useMutation({
    mutationFn: SuperadminService.banUser,
    onSuccess: () => {
      invalidateUsers();
      toast.success(t("users.ban.success"));
    },
    onError: () => toast.error(t("users.ban.error")),
  });

  const unbanMutation = useMutation({
    mutationFn: SuperadminService.unbanUser,
    onSuccess: () => {
      invalidateUsers();
      toast.success(t("users.unban.success"));
    },
    onError: () => toast.error(t("users.unban.error")),
  });

  const deleteMutation = useMutation({
    mutationFn: SuperadminService.deleteUser,
    onSuccess: () => {
      invalidateUsers();
      toast.success(t("users.delete.success"));
    },
    onError: () => toast.error(t("users.delete.error")),
  });

  const toggleSuperadminMutation = useMutation({
    mutationFn: SuperadminService.toggleSuperadmin,
    onSuccess: (data) => {
      invalidateUsers();
      toast.success(
        data.isSuperadmin
          ? t("users.superadmin.grantSuccess")
          : t("users.superadmin.revokeSuccess"),
      );
    },
    onError: () => toast.error(t("users.superadmin.error")),
  });

  const handleAction = useCallback(
    (action: string, user: SuperadminUser) => {
      setSelectedUser(user);
      switch (action) {
        case "view":
          setDetailOpen(true);
          break;
        case "ban":
          setBanDialogOpen(true);
          break;
        case "unban":
          unbanMutation.mutate(user.id);
          break;
        case "delete":
          setDeleteDialogOpen(true);
          break;
        case "toggleSuperadmin":
          setSuperadminDialogOpen(true);
          break;
      }
    },
    [unbanMutation],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("users.title")}</h1>
        <p className="text-muted-foreground">{t("users.description")}</p>
      </div>

      <UserFilters
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        status={params.status}
        role={params.role}
        twoFactor={params.twoFactor}
        onFilterChange={(key, value) =>
          updateParams({ [key]: value, page: 1 })
        }
        onClear={() =>
          updateParams({
            search: "",
            status: "all",
            role: "all",
            twoFactor: "all",
            page: 1,
          })
        }
      />

      <UserTable
        users={data?.users ?? []}
        pagination={data?.pagination}
        isLoading={isLoading}
        sortBy={params.sortBy}
        sortOrder={params.sortOrder}
        onSort={(sortBy, sortOrder) => updateParams({ sortBy, sortOrder })}
        onPageChange={(page) => updateParams({ page })}
        onAction={handleAction}
      />

      {selectedUser && (
        <>
          <UserDetail
            userId={selectedUser.id}
            summaryUser={selectedUser}
            open={detailOpen}
            onOpenChange={setDetailOpen}
          />

          <BanUserDialog
            user={selectedUser}
            open={banDialogOpen}
            onOpenChange={setBanDialogOpen}
            onConfirm={(reason, expiresAt) => {
              banMutation.mutate({
                userId: selectedUser.id,
                reason,
                expiresAt,
              });
              setBanDialogOpen(false);
            }}
            isLoading={banMutation.isPending}
          />

          <DeleteUserDialog
            user={selectedUser}
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            onConfirm={(password) => {
              deleteMutation.mutate({
                userId: selectedUser.id,
                password,
              });
              setDeleteDialogOpen(false);
            }}
            isLoading={deleteMutation.isPending}
          />

          <ToggleSuperadminDialog
            user={selectedUser}
            open={superadminDialogOpen}
            onOpenChange={setSuperadminDialogOpen}
            onConfirm={(password) => {
              toggleSuperadminMutation.mutate({
                userId: selectedUser.id,
                password,
              });
              setSuperadminDialogOpen(false);
            }}
            isLoading={toggleSuperadminMutation.isPending}
          />
        </>
      )}
    </div>
  );
}
